import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { ListChecks, BarChart3 } from "lucide-react";
import DailyView from "./appel/DailyView";
import RecapView from "./appel/RecapView";

function useAppelLoaders(classId, view, date, slot) {
  const [data, setData] = useState(null);
  const [recap, setRecap] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadDaily = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/appel", { params: { class_id: classId, date, slot } });
      setData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [classId, date, slot]);

  const loadRecap = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/classes/${classId}/appel-summary`);
      setRecap(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [classId]);

  useEffect(() => {
    if (view === "daily") loadDaily();
    else loadRecap();
  }, [view, loadDaily, loadRecap]);

  return { data, recap, loading, loadDaily };
}

function useAppelActions(classId, date, slot, loadDaily) {
  const [busy, setBusy] = useState(null);

  const mark = async (studentId, status) => {
    setBusy(studentId);
    try {
      await api.post("/appel/mark", { class_id: classId, student_id: studentId, date, slot, status });
      await loadDaily();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(null);
    }
  };

  const validate = async () => {
    setBusy("validate");
    try {
      await api.post("/appel/validate", { class_id: classId, date, slot });
      toast.success("Appel validé — la direction est informée");
      await loadDaily();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(null);
    }
  };

  return { busy, mark, validate };
}

export default function AppelPanel({ classId, canEdit }) {
  const [view, setView] = useState("daily");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [slot, setSlot] = useState("s0810");
  const { data, recap, loading, loadDaily } = useAppelLoaders(classId, view, date, slot);
  const { busy, mark, validate } = useAppelActions(classId, date, slot, loadDaily);

  return (
    <div data-testid="appel-panel">
      <div className="flex gap-1 border-b border-[var(--parchment-2)] mb-4">
        <SubTab active={view === "daily"} onClick={() => setView("daily")} icon={<ListChecks size={14} />} label="Appel du jour" testId="subtab-daily" />
        <SubTab active={view === "recap"} onClick={() => setView("recap")} icon={<BarChart3 size={14} />} label="Bilan cumulé" testId="subtab-recap" />
      </div>

      {view === "daily" ? (
        <DailyView
          date={date}
          setDate={setDate}
          slot={slot}
          setSlot={setSlot}
          data={data}
          loading={loading}
          busy={busy}
          canEdit={canEdit}
          onMark={mark}
          onValidate={validate}
        />
      ) : (
        <RecapView recap={recap} loading={loading} />
      )}
    </div>
  );
}

function SubTab({ active, onClick, icon, label, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition ${
        active ? "border-[var(--maroon)] text-[var(--maroon)]" : "border-transparent text-[var(--ink-soft)] hover:text-[var(--ink)]"
      }`}
    >
      {icon} {label}
    </button>
  );
}
